import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Autocomplete, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useLoader from "../../../hooks/useLoader.js";
import useModal from "../../../hooks/useModal.js";
import { formatDateToYYYYMMDD, formatDisplayPeriod, formatDisplayMonth } from "../../../Helper/functionHelper.js";
import { getEmployees } from "../../../services/employee.js";
import { runMonthlyParity } from "../../../services/monthlyParity.js";

const employeeLabel = (emp) =>
  emp ? `${[emp._FirstName, emp._MiddleName, emp._LastName].filter(Boolean).join(" ")} (${emp._UserEmployeeID})` : "";

const money = (v) => (v === null || v === undefined ? "N/A" : `₹${Number(v).toFixed(2)}`);

const DiffRow = ({ label, diff }) => {
  if (!diff) {
    return (
      <div className="col-6 mb-1">
        {label}: <span className="text-muted">no CI4 slip for this month</span>
      </div>
    );
  }
  return (
    <div className="col-6 mb-1">
      {label}: Node {money(diff.node)} | CI4 {money(diff.ci4)} |{" "}
      <span className={diff.matches ? "text-success" : "text-danger"}>
        {diff.matches ? "Matches" : `Δ ${diff.delta}`}
      </span>
    </div>
  );
};

const RunMonthlyParity = ({ refresh }) => {
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [month, setMonth] = useState(new Date());
  const [isBusy, setIsBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await getEmployees();
        setEmployees(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Failed to load employees");
      }
    };
    fetchEmployees();
  }, []);

  const handleRun = async () => {
    if (!selectedEmployee) {
      toast.error("Please select an employee");
      return;
    }

    setIsBusy(true);
    showLoader();
    try {
      const response = await runMonthlyParity(selectedEmployee._UserID, formatDateToYYYYMMDD(month));
      setResult(response?.data?.data);
      toast.success("Parity check run successfully");
      refresh?.();
    } catch (error) {
      console.error("Error running monthly parity:", error);
      toast.error(error?.response?.data?.message || "Failed to run parity check");
      setResult(null);
    } finally {
      setIsBusy(false);
      hideLoader();
    }
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Employee <span className="text-danger">*</span>
          </label>
          <Autocomplete
            size="small"
            options={employees}
            getOptionLabel={employeeLabel}
            isOptionEqualToValue={(opt, val) => opt._UserID === val._UserID}
            value={selectedEmployee}
            onChange={(e, value) => {
              setSelectedEmployee(value);
              setResult(null);
            }}
            disabled={isBusy}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select employee" />}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label d-block" style={{ fontWeight: 600, fontSize: "12px" }}>
            Month
          </label>
          <DatePicker
            views={["year", "month"]}
            openTo="month"
            format="MMM-yyyy"
            value={month}
            onChange={(value) => {
              setMonth(value);
              setResult(null);
            }}
            disabled={isBusy}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </div>
      </div>

      <div className="form-text mb-3">
        Requires the employee to have a monthly payroll structure assigned (pms_users._Payroll). Computes Node's
        version of that month's payroll and diffs it against CI4's real payslip, if one exists - nothing here
        touches live monthly payroll.
      </div>

      {result && (
        <div className="p-3 mb-3" style={{ backgroundColor: "#f9f9f9", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
          <div className="fw-semibold mb-2">{result.period?.start ? formatDisplayPeriod(result.period.start, result.period.end) : formatDisplayMonth(result.period?.month_year)}</div>

          <div className="row" style={{ fontSize: "13px" }}>
            <DiffRow label="Gross" diff={result.diff?.gross} />
            <DiffRow label="Deduction" diff={result.diff?.deduction} />
            <DiffRow label="Net Pay" diff={result.diff?.net_pay} />
            <DiffRow label="Days Worked" diff={result.diff?.days_worked} />
          </div>

          {result.not_yet_compared?.length > 0 && (
            <>
              <hr />
              <div className="fw-semibold mb-1" style={{ fontSize: "12px" }}>
                Not yet compared (excluded from this diff):
              </div>
              <ul className="mb-0" style={{ fontSize: "12px" }}>
                {result.not_yet_compared.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2" style={{ borderTop: "1px solid #e0e0e0", paddingTop: "12px" }}>
        <button type="button" onClick={hideModal} className="btn btn-secondary btn-sm" disabled={isBusy}>
          Close
        </button>
        <button type="button" className="btn btn-success btn-sm" disabled={isBusy} onClick={handleRun}>
          {isBusy ? "Running..." : "Run Parity Check"}
        </button>
      </div>
    </div>
  );
};

export default RunMonthlyParity;
